/**
 * One field over several entities (multi-select), as pure functions: the
 * value the field shows, and the value an edit sets on each entity. The
 * pair mirrors Unreal's multi-object details: equal values show, differing
 * ones show "Multiple Values" (here `MIXED`), and a vec3 differs per axis.
 *
 * Lists and other structured values are compared whole: merging them by
 * position would pair up unrelated items.
 */
import type { PropType } from "../../world/schema/propTypes";
import { isMixed, MIXED } from "../widgets/mixed";

/** The value every entity has, `MIXED` where they differ (per axis for a vec3). */
export function sharedValue(prop: PropType, values: readonly unknown[]): unknown {
  const [first, ...rest] = values;
  if (rest.every((value) => sameValue(value, first))) return first;
  if (prop.type === "vec3") return sharedAxes(values);
  return MIXED;
}

/**
 * What an edit sets on one entity: the edited value, with any part the edit
 * left `MIXED` (a vec3's other axes) taken from the entity's own value.
 */
export function resolveEdit(edited: unknown, own: unknown): unknown {
  if (isMixed(edited)) return own;
  if (Array.isArray(edited) && Array.isArray(own) && edited.length === own.length) {
    const ownParts: readonly unknown[] = own;
    return edited.map((part: unknown, i) => (isMixed(part) ? ownParts[i] : part));
  }
  return edited;
}

function sharedAxes(values: readonly unknown[]): unknown[] {
  const vectors = values as readonly (readonly number[])[];
  return [0, 1, 2].map((axis) => {
    const first = vectors[0]?.[axis];
    return vectors.every((vector) => vector[axis] === first) ? first : MIXED;
  });
}

/** Prop values are JSON, so equal content means equal serialization. */
function sameValue(a: unknown, b: unknown): boolean {
  return a === b || JSON.stringify(a) === JSON.stringify(b);
}
